import { z } from 'zod';
// ---------------------------------------------------------------------------
// V2 evidence + version schemas. Shapes mirror the defender backend
// (`@whyhotel/adyen-chargeback-defender`'s `src/schemas.ts`) — kept loose with
// `z.unknown()` on the deepest fields so a backend schema bump doesn't break
// the SPA. The renderer code knows which keys it expects.
// ---------------------------------------------------------------------------
// Full nested Apaleo details as returned by the defender backend. Kept loose
// with `z.unknown()` on nested fields so a backend schema bump doesn't break
// the SPA — components read known keys with optional chaining.
const ApaleoDetailsSchema = z
    .object({
    isResolved: z.boolean().optional(),
    reservation: z.record(z.string(), z.unknown()).nullable().optional(),
    folio: z.record(z.string(), z.unknown()).nullable().optional(),
    property: z.record(z.string(), z.unknown()).optional(),
    ratePlan: z.record(z.string(), z.unknown()).optional(),
    cancellationPolicy: z.record(z.string(), z.unknown()).optional(),
    noShowPolicy: z.record(z.string(), z.unknown()).optional()
})
    .passthrough();
const AuthorizationDetailsSchema = z
    .object({
    threeDAuthenticated: z.string().optional(),
    threeDOffered: z.string().optional(),
    liabilityShift: z.string().optional(),
    avsResult: z.string().optional(),
    cvcResult: z.string().optional(),
    fraudResultType: z.string().optional(),
    fraudRiskLevel: z.string().optional(),
    totalFraudScore: z.string().optional(),
    recurringProcessingModel: z.string().optional(),
    shopperInteraction: z.string().optional(),
    paymentMethod: z.string().optional(),
    cardBin: z.string().optional(),
    cardSummary: z.string().optional(),
    cardIssuingCountry: z.string().optional()
})
    .passthrough();
const OriginalAuthorizationSchema = z
    .object({
    pspReference: z.string(),
    date: z.string().optional(),
    amountValue: z.number().optional(),
    currency: z.string().optional(),
    success: z.boolean().optional(),
    details: AuthorizationDetailsSchema.optional()
})
    .passthrough();
const RefundDetailsSchema = z.object({
    refundedAmountValue: z.number(),
    isFullyRefunded: z.boolean(),
    isPartiallyRefunded: z.boolean()
});
const AdyenTransactionDetailsSchema = z
    .object({
    authorizationPspReference: z.string().optional(),
    authorizationDate: z.string().optional(),
    authorizationAmountValue: z.number().optional(),
    authorizationCurrency: z.string().optional(),
    authorizationSuccess: z.boolean().optional(),
    authorizationDetails: AuthorizationDetailsSchema.optional(),
    originalAuthorization: OriginalAuthorizationSchema.optional(),
    refundDetails: RefundDetailsSchema.optional(),
    fetchedAt: z.string().optional()
})
    .passthrough();
const EvidenceStatusSchema = z.enum(['ok', 'unavailable', 'error']);
const IdVerificationEvidenceSchema = z.object({
    status: EvidenceStatusSchema,
    verified: z.boolean().optional(),
    method: z.string().optional(),
    verifiedAt: z.string().optional(),
    provider: z.string().optional()
});
const TocAcceptanceEvidenceSchema = z.object({
    status: EvidenceStatusSchema,
    accepted: z.boolean().optional(),
    acceptedAt: z.string().optional(),
    policyVersion: z.string().optional(),
    documentTypes: z.array(z.string()).optional()
});
const GuestCommsEvidenceSchema = z.object({
    status: EvidenceStatusSchema,
    messageCount: z.number().optional(),
    firstAt: z.string().optional(),
    lastAt: z.string().optional(),
    channels: z.array(z.string()).optional(),
    topicSummary: z.string().optional()
});
const DoorAccessEvidenceSchema = z.object({
    status: EvidenceStatusSchema,
    entryEventCount: z.number().optional(),
    firstEntryAt: z.string().optional(),
    lastEntryAt: z.string().optional(),
    withinStayWindow: z.boolean().optional(),
    provider: z.string().optional()
});
const AdditionalEvidenceSchema = z.object({
    idVerification: IdVerificationEvidenceSchema.optional(),
    tocAcceptance: TocAcceptanceEvidenceSchema.optional(),
    guestComms: GuestCommsEvidenceSchema.optional(),
    doorAccess: DoorAccessEvidenceSchema.optional(),
    fetchedAt: z.string().optional()
});
const HitlFlagSchema = z.object({
    code: z.string(),
    message: z.string()
});
// Scheme + reason-code catalog entry, attached to the case DTO by
// `get-chargeback-case`. Kept optional — the backend omits the field when the
// scheme + code combination isn't in the catalog, and the Defense Details
// card gracefully degrades to the v5 layout when undefined.
export const DefenseRequirementSchema = z.object({
    requirement: z.string(),
    group: z.string().optional()
});
export const SchemeReasonInfoSchema = z.object({
    schemeCode: z.string(),
    reasonCode: z.string(),
    reason: z.string(),
    description: z.string(),
    category: z.string(),
    isDefendable: z.boolean(),
    timeframe: z.string(),
    defenseRequirements: z.array(DefenseRequirementSchema)
});
const DefenseDocVersionSchema = z.object({
    version: z.number().int().positive(),
    createdAt: z.string(),
    createdBy: z.string(),
    operatorNote: z.string().optional(),
    summaryS3Key: z.string(),
    documentS3Key: z.string(),
    sidecarS3Key: z.string().optional(),
    documentTypeCode: z.string().optional(),
    hitlFlags: z.array(HitlFlagSchema).optional(),
    supersededByVersion: z.number().int().positive().optional()
});
export const ChargebackCaseSchema = z.object({
    pspReference: z.string(),
    merchantReference: z.string().optional(),
    originalReference: z.string().optional(),
    eventDate: z.string().optional(),
    amount: z.number().optional(),
    currency: z.string().optional(),
    chargebackReasonCode: z.string().optional(),
    chargebackSchemeCode: z.string().optional(),
    reason: z.string().optional(),
    defensePeriodEndsAt: z.string().optional(),
    defenseStatus: z.string().optional(),
    defenseResult: z.string().optional(),
    defenseDocTemplateId: z.string().optional(),
    defenseReasonCodeSelected: z.string().optional(),
    reservationId: z.string().optional(),
    folioId: z.string().optional(),
    propertyId: z.string().optional(),
    // Persisted evidence + version history.
    apaleoDetails: ApaleoDetailsSchema.optional(),
    adyenTransactionDetails: AdyenTransactionDetailsSchema.optional(),
    additionalEvidence: AdditionalEvidenceSchema.optional(),
    applicableDefenseReasons: z.array(z.unknown()).optional(),
    hitlFlags: z.array(HitlFlagSchema).optional(),
    reviewDecision: z.string().optional(),
    reviewerNote: z.string().optional(),
    reviewedAt: z.string().optional(),
    submittedAt: z.string().optional(),
    submittedReasonCode: z.string().optional(),
    defenseDocumentVersions: z.array(DefenseDocVersionSchema).default([]),
    currentDocumentVersion: z.number().int().positive().optional(),
    signedDocumentUrl: z.string().optional(),
    schemeReasonInfo: SchemeReasonInfoSchema.optional(),
    // ARN of the most recently started Step Functions execution for this case.
    // Rendered read-only at the bottom of the Defense Details card as a jumping-off
    // point to the SFN console.
    executionId: z.string().optional()
});
// List DTO carries the RAW status fields (defenseStatus / defenseResult) plus a
// derived hasPendingReview flag. The human-friendly display label is computed
// client-side in src/status/displayStatus.ts — never in the backend.
export const ChargebackListItemSchema = z.object({
    pspReference: z.string(),
    originalReference: z.string().optional(),
    merchantReference: z.string().optional(),
    eventDate: z.string().optional(),
    defensePeriodEndsAt: z.string().optional(),
    amount: z.number().optional(),
    currency: z.string().optional(),
    chargebackReasonCode: z.string().optional(),
    chargebackSchemeCode: z.string().optional(),
    reason: z.string().optional(),
    defenseStatus: z.string().optional(),
    defenseResult: z.string().optional(),
    reservationId: z.string().optional(),
    propertyId: z.string().optional(),
    hasPendingReview: z.boolean()
});
// Adyen webhook event log for the payment lifecycle attached to a chargeback.
// Backend returns the outer envelope only — raw `payload` (which carries card
// metadata / PII) is intentionally stripped server-side.
export const TransactionEventSchema = z.object({
    pspReference: z.string(),
    originalPspReference: z.string().optional(),
    merchantAccountCode: z.string().optional(),
    eventCode: z.string(),
    eventDate: z.string(),
    success: z.boolean().optional()
});
